import { Box } from "@mui/system";
import { Fragment } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import TextInput from "shared/ui/TextInput";
import { selectSurveyInfo } from "store/slicers/campaignDetail";
import { Skeleton } from "@mui/material";
import BasicSelect from "shared/ui/Select";
import { requiredRules } from "shared/helpers/validators";
import { selectCampaignLoading } from "store/slicers/common";
import { ECampaignSurveyType } from "pages/dashboard/CampaignDetails/questions/LeftSidebar/constants";
import {
  CommentCharacterLimit,
  MultipleChoiceLimitOptions,
} from "pages/dashboard/CampaignDetails/questions/RightSidebar/constants";

const Config = () => {
  const { watch, trigger } = useFormContext();
  const surveyInfo = useSelector(selectSurveyInfo);
  const campaignLoadingState = useSelector(selectCampaignLoading);

  const multipleRule = {
    max: {
      message: `The value must be less than or equal to 
  ${watch("answers")?.length}`,
      value: watch("answers")?.length,
    },
    min: {
      message: "The value must be more than 1",
      value: 1,
    },
    validate: () => {
      return (
        Number(watch("multipleConfig.multipleMin")) <
          Number(watch("multipleConfig.multipleMax")) ||
        "Min limit must be less than Max limit"
      );
    },
  };

  const multipleRuleExact = {
    max: {
      message: `The value must be less than or equal to 
  ${watch("answers")?.length}`,
      value: watch("answers")?.length,
    },
    min: {
      message: "The value must be more than 1",
      value: 1,
    },
  };

  const commentRules = {
    validate: () => {
      return (
        Number(watch("commentConfig.commentMin")) <
          Number(watch("commentConfig.commentMax")) ||
        "Comment min limit must be less than max limit"
      );
    },
  };

  const metricRules = {
    max: {
      value: 10,
      message: "Value must be less than or equal to 10",
    },
    validate: () => {
      if (
        Number(watch("metricConfig.customEndLength")) <
        Number(watch("metricConfig.customStartLength"))
      ) {
        return "Range start must be less than range end";
      }

      if (
        Number(watch("metricConfig.customEndLength")) -
          Number(watch("metricConfig.customStartLength")) <
        2
      ) {
        return "The range must include at least 3 values";
      }
      return true;
    },
  };

  const handleBlurTrigger = () => {
    trigger();
  };

  return (
    <Box>
      {campaignLoadingState ? (
        <>
          <Skeleton
            variant="rounded"
            animation="wave"
            width={"100%"}
            height="53px"
            sx={{ mb: 2 }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            width={"100%"}
            height="53px"
            sx={{ mb: 2 }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            width={"100%"}
            height="53px"
            sx={{ mb: 2 }}
          />
        </>
      ) : (
        <>
          {surveyInfo.details?.type !== Number(ECampaignSurveyType.Final) && (
            <Fragment>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}
              >
                {surveyInfo.details?.type ===
                  Number(ECampaignSurveyType.Comment) && (
                  <TextInput
                    name="commentText"
                    label="Comment Text"
                    rules={{
                      ...requiredRules,
                      maxLength: {
                        value: 30,
                        message: "Max length is 14 symbols",
                      },
                      minLength: {
                        value: 2,
                        message: "Min length is 2 symbols",
                      },
                    }}
                  />
                )}
              </Box>

              {surveyInfo.details?.type ===
                Number(ECampaignSurveyType.MultipleChoice) && (
                <Fragment>
                  <BasicSelect
                    size="small"
                    name="multipleConfig.multipleType"
                    label={"Choice Limit"}
                    labelProp="name"
                    valueProp="value"
                    defaultValue={1}
                    options={MultipleChoiceLimitOptions}
                  />
                  {watch("multipleConfig.multipleType") === 2 && (
                    <Box mt={2}>
                      <TextInput
                        type="number"
                        name="multipleConfig.multipleExact"
                        label="Limit"
                        rules={{
                          ...requiredRules,
                          ...multipleRuleExact,
                        }}
                      />
                    </Box>
                  )}
                  {watch("multipleConfig.multipleType") === 3 && (
                    <Fragment>
                      <Box mt={2}>
                        <TextInput
                          type="number"
                          name="multipleConfig.multipleMin"
                          label="Min limit"
                          onBlur={handleBlurTrigger}
                          rules={{ ...requiredRules, ...multipleRule }}
                        />
                      </Box>
                      <Box mt={2}>
                        <TextInput
                          type="number"
                          name="multipleConfig.multipleMax"
                          label="Max limit"
                          onBlur={handleBlurTrigger}
                          rules={{ ...requiredRules, ...multipleRule }}
                        />
                      </Box>
                    </Fragment>
                  )}
                </Fragment>
              )}
              {surveyInfo.details?.type ===
                Number(ECampaignSurveyType.Comment) && (
                <Fragment>
                  <BasicSelect
                    size="small"
                    name="commentConfig.commentType"
                    label={"Character Limit"}
                    labelProp="name"
                    valueProp="value"
                    defaultValue={1}
                    options={CommentCharacterLimit}
                  />
                  {watch("commentConfig.commentType") === 2 && (
                    <Fragment>
                      <Box mt={2}>
                        <TextInput
                          name="commentConfig.commentMin"
                          label="Min limit"
                          onBlur={handleBlurTrigger}
                          rules={{ ...requiredRules, ...commentRules }}
                        />
                      </Box>
                      <Box mt={2}>
                        <TextInput
                          name="commentConfig.commentMax"
                          label="Max limit"
                          onBlur={handleBlurTrigger}
                          rules={{ ...requiredRules, ...commentRules }}
                        />
                      </Box>
                    </Fragment>
                  )}
                </Fragment>
              )}
              {(surveyInfo.details?.type ===
                Number(ECampaignSurveyType.Rating) ||
                surveyInfo.details?.type ===
                  Number(ECampaignSurveyType.CustomStar)) && (
                <Fragment>
                  <Box mt={2}>
                    <TextInput
                      name="metricConfig.customStartLength"
                      label="Range Start"
                      rules={{ ...requiredRules, ...metricRules }}
                    />
                  </Box>
                  <Box mt={2}>
                    <TextInput
                      name="metricConfig.customEndLength"
                      label="Range End"
                      rules={{ ...requiredRules, ...metricRules }}
                    />
                  </Box>
                </Fragment>
              )}
            </Fragment>
          )}
        </>
      )}
    </Box>
  );
};

export default Config;
